import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Home, Building2, ArrowRight, ArrowLeft, Check, Snowflake, CheckCircle2, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Captcha } from "@/components/Captcha";
import api, { apiError, PHONE, PHONE_TEL } from "@/lib/api";

const variants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

function Choice({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`flex w-full items-center gap-4 rounded-2xl border-2 p-5 text-left transition-all ${active ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200 hover:border-slate-300"}`}>
      {Icon && <div className={`rounded-xl p-3 ${active ? "bg-[#1D9BF0] text-white" : "bg-slate-100 text-slate-500"}`}><Icon className="h-6 w-6" /></div>}
      <div className="flex-1">
        <p className="font-bold text-[#263871]">{title}</p>
        {sub && <p className="text-sm text-slate-500">{sub}</p>}
      </div>
      {active && <Check className="h-5 w-5 text-[#1D9BF0]" />}
    </button>
  );
}

export default function InstallFunnel() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState({ token: "", answer: "", website: "" });
  const [f, setF] = useState({
    client_type: "", nb_pieces_vivre: "", nb_chambres: "", surface: "", combles: "",
    chauffage_actuel: "", nb_clim: "", budget: "", delai: "",
    address: "", address_lat: null, address_lon: null,
    client_name: "", client_phone: "", client_email: "", details: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const isPro = f.client_type === "entreprise";
  const steps = [
    { key: "type", valid: !!f.client_type },
    { key: "logement", valid: !!f.nb_pieces_vivre },
    { key: "projet", valid: !!f.nb_clim },
    { key: "adresse", valid: !!f.address },
    { key: "contact", valid: f.client_name && f.client_phone },
  ];
  const total = steps.length;
  const progress = Math.round(((step + 1) / total) * 100);

  const next = () => {
    if (!steps[step].valid) return toast.error("Merci de compléter cette étape");
    setStep((s) => Math.min(total - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!steps[4].valid) return toast.error("Nom et téléphone requis");
    if (!captcha.answer) return toast.error("Merci de répondre à la question anti-spam");
    setBusy(true);
    try {
      await api.post("/leads/installation", {
        ...f,
        nb_pieces_vivre: f.nb_pieces_vivre ? Number(f.nb_pieces_vivre) : null,
        nb_chambres: f.nb_chambres ? Number(f.nb_chambres) : null,
        nb_clim: f.nb_clim === "?" ? 1 : Number(f.nb_clim) || 1,
        captcha_token: captcha.token, captcha_answer: captcha.answer, website: captcha.website,
      });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F1F5F9] p-6 text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md rounded-3xl bg-white p-10 shadow-xl">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
          <h1 className="mt-4 text-2xl font-extrabold text-[#263871]">Demande envoyée !</h1>
          <p className="mt-2 text-slate-600">Un installateur certifié proche de <strong>{f.address}</strong> va vous contacter pour fixer un rendez-vous et établir votre devis gratuit.</p>
          <Button data-testid="funnel-home" onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#263871] px-8">Retour à l'accueil</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="cl-glass sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-8">
        <Logo />
        <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 text-sm font-semibold text-[#263871]"><Phone className="h-4 w-4 text-[#1D9BF0]" />{PHONE}</a>
      </header>

      <div className="mx-auto max-w-xl px-4 py-8 sm:py-12">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Demande d'installation</span><span>{step + 1}/{total}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
            <motion.div className="h-full rounded-full bg-[#1D9BF0]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
              {step === 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[#263871]">Votre projet concerne…</h2>
                  <p className="mb-5 text-sm text-slate-500">Pour mieux orienter votre installation.</p>
                  <div className="space-y-3">
                    <Choice testid="funnel-type-particulier" active={f.client_type === "particulier"} onClick={() => { set("client_type", "particulier"); }} icon={Home} title="Un particulier" sub="Maison ou appartement" />
                    <Choice testid="funnel-type-entreprise" active={f.client_type === "entreprise"} onClick={() => { set("client_type", "entreprise"); }} icon={Building2} title="Une entreprise" sub="Bureaux, commerce, local" />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h2 className="text-xl font-bold text-[#263871]">{isPro ? "Votre local" : "Votre logement"}</h2>
                  <p className="mb-5 text-sm text-slate-500">Quelques infos sur les surfaces à climatiser.</p>
                  <div className="space-y-4">
                    <div><Label>{isPro ? "Nombre de pièces / bureaux" : "Pièces à vivre (salon, séjour…)"}</Label><Input data-testid="funnel-pieces" type="number" min={0} value={f.nb_pieces_vivre} onChange={(e) => set("nb_pieces_vivre", e.target.value)} className="mt-1 h-12 rounded-xl" /></div>
                    {!isPro && <div><Label>Nombre de chambres</Label><Input data-testid="funnel-chambres" type="number" min={0} value={f.nb_chambres} onChange={(e) => set("nb_chambres", e.target.value)} className="mt-1 h-12 rounded-xl" /></div>}
                    <div><Label>Surface approximative</Label><Input data-testid="funnel-surface" value={f.surface} onChange={(e) => set("surface", e.target.value)} placeholder="ex: 90 m²" className="mt-1 h-12 rounded-xl" /></div>
                    <div>
                      <Label>Combles / accès disponible pour le passage des liaisons</Label>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        {["Oui", "Non", "Aménageables"].map((c) => (
                          <button key={c} type="button" data-testid={`funnel-combles-${c}`} onClick={() => set("combles", c)} className={`rounded-xl border-2 py-2.5 text-sm font-semibold ${f.combles === c ? "border-[#1D9BF0] bg-[#E0F2FE] text-[#263871]" : "border-slate-200 text-slate-600"}`}>{c}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="text-xl font-bold text-[#263871]">Votre projet de climatisation</h2>
                  <p className="mb-5 text-sm text-slate-500">Aidez l'installateur à préparer son devis.</p>
                  <div className="space-y-4">
                    <div>
                      <Label className="flex items-center gap-1.5"><Snowflake className="h-4 w-4 text-[#1D9BF0]" />Combien de climatiseurs souhaitez-vous ?</Label>
                      <div className="mt-2 grid grid-cols-5 gap-2">
                        {["1", "2", "3", "4", "?"].map((n) => (
                          <button key={n} type="button" data-testid={`funnel-nbclim-${n}`} onClick={() => set("nb_clim", n)} className={`rounded-xl border-2 py-2.5 text-sm font-bold ${f.nb_clim === n ? "border-[#1D9BF0] bg-[#E0F2FE] text-[#263871]" : "border-slate-200 text-slate-600"}`}>{n === "?" ? "Je ne sais pas" : n}</button>
                        ))}
                      </div>
                    </div>
                    <div><Label>Chauffage actuel</Label><Input data-testid="funnel-chauffage" value={f.chauffage_actuel} onChange={(e) => set("chauffage_actuel", e.target.value)} placeholder="ex: électrique, gaz, pompe à chaleur…" className="mt-1 h-12 rounded-xl" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Budget estimé</Label><Input data-testid="funnel-budget" value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="optionnel" className="mt-1 h-12 rounded-xl" /></div>
                      <div><Label>Délai souhaité</Label><Input data-testid="funnel-delai" value={f.delai} onChange={(e) => set("delai", e.target.value)} placeholder="ex: 1 mois" className="mt-1 h-12 rounded-xl" /></div>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="text-xl font-bold text-[#263871]">Où se situe le projet ?</h2>
                  <p className="mb-5 text-sm text-slate-500">Nous cherchons un installateur proche de vous.</p>
                  <AddressAutocomplete testid="funnel-address" value={f.address} onSelect={({ label, lat, lon }) => setF((p) => ({ ...p, address: label, address_lat: lat, address_lon: lon }))} />
                  <Textarea data-testid="funnel-details" value={f.details} onChange={(e) => set("details", e.target.value)} placeholder="Détails complémentaires (optionnel)" className="mt-3" />
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="text-xl font-bold text-[#263871]">Vos coordonnées</h2>
                  <p className="mb-5 text-sm text-slate-500">Pour que l'installateur vous recontacte.</p>
                  <div className="space-y-4">
                    <div><Label>Nom complet</Label><Input data-testid="funnel-name" value={f.client_name} onChange={(e) => set("client_name", e.target.value)} className="mt-1 h-12 rounded-xl" /></div>
                    <div><Label>Téléphone</Label><Input data-testid="funnel-phone" value={f.client_phone} onChange={(e) => set("client_phone", e.target.value)} className="mt-1 h-12 rounded-xl" /></div>
                    <div><Label>Email (optionnel)</Label><Input data-testid="funnel-email" type="email" value={f.client_email} onChange={(e) => set("client_email", e.target.value)} className="mt-1 h-12 rounded-xl" /></div>
                    <Captcha testid="funnel-captcha" onChange={(v) => setCaptcha((p) => ({ ...p, ...v }))} />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex gap-3">
            {step > 0 && <Button variant="outline" onClick={back} className="h-12 rounded-full border-2 px-6"><ArrowLeft className="mr-1 h-4 w-4" />Retour</Button>}
            {step < total - 1 ? (
              <Button data-testid="funnel-next" onClick={next} className="h-12 flex-1 rounded-full bg-[#263871] hover:bg-[#35508F]">Continuer <ArrowRight className="ml-1 h-4 w-4" /></Button>
            ) : (
              <Button data-testid="funnel-submit" onClick={submit} disabled={busy} className="h-12 flex-1 rounded-full bg-[#1D9BF0] hover:bg-[#57A4FF]">{busy ? "Envoi…" : "Recevoir mon devis gratuit"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
